import { useState } from 'react'
import { Link, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { useClub } from '../club/ClubProvider.tsx'
import { EXCEPTION_DAYS, EXCEPTION_RULE, EXCEPTION_TITLE_KEY, exceptionHint, triageProfile, useHomeSignals, type ExceptionRow } from '../data/exceptions.ts'
import { useAreas, type AreaRow } from '../data/geo.ts'
import { useServiceDue } from '../data/meters.ts'
import { useRpc } from '../data/people.ts'
import { say, usePlan } from '../data/plan.ts'
import { useNames } from '../data/planUi.ts'
import { useSettingsConfirmations } from '../data/settingsDone.ts'
import { useWorkIntervals, useWorkSessions } from '../data/work.ts'
import { reviewTarget, spanText, type ReviewItem } from '../data/workTime.ts'
import { formatLocal } from '../lib/time.ts'
import { BackLink, Banner, errorText, Field, PendingBanner, Pill } from '../ui/components.tsx'
import { useWorkshopAction } from '../ui/hooks.ts'
import { profileOf } from '../ui/profile.ts'

const SEVERITY_TONE = { high: 'red', medium: 'amber', low: 'gray' } as const
type Tab = 'queue' | 'signals'

/** The review queue (GK-018): what the server could not settle by itself. It is the SERVER's list (online only), read through the review RPC; each action waits for the server's
 *  verdict and the list is read again after it. The head greenkeeper accepts or rejects a period that waits for review; anybody who may close items marks one as handled.
 *  M16-A adds a second tab, "Signaux": the same lines as Today's "À traiter" (the device's own counts and the server's signals), each with a "Vu" that puts it aside on
 *  this device only (localStorage, never club data: an acknowledgement that silenced a signal server-side would contradict "the app decides nothing"). The tab is the
 *  direction's and the mechanic's, like the section; `?tab=signals` opens it (Today's "Voir les signaux"). */
export function ReviewQueuePage() {
  const { t } = useTranslation()
  const { org, site, can, kiosk } = useClub()
  const allowed = can('review.read')
  const q = useRpc<ReviewItem[]>('review_queue', { p_org: org.org_id, p_site: site.site_id }, allowed)
  const sessions = useWorkSessions(); const intervals = useWorkIntervals()
  const [done, setDone] = useState<string | null>(null)
  const search = useSearch({ strict: false }) as { tab?: string }
  const profile = profileOf(site.roles, !!kiosk)
  const signals = allowed && triageProfile(profile)
  const [tab, setTab] = useState<Tab>(search.tab === 'signals' ? 'signals' : 'queue')
  const back = <BackLink to="/">← {t('common.today')}</BackLink>
  if (!allowed) return <section className="flex flex-col gap-3" data-testid="review-page">{back}<Banner tone="warn"><span data-testid="review-denied">{t('reviewQueue.notAllowed')}</span></Banner></section>
  const items = q.data
  const sessionJob = new Map((sessions ?? []).map((s) => [s.id, s.job_id]))
  const intervalSession = new Map((intervals ?? []).map((i) => [i.id, i.work_session_id]))
  const pendingIntervals = new Set((intervals ?? []).filter((i) => i.review_state === 'pending' && !i.superseded_by_id).map((i) => i.id))
  const shownTab: Tab = signals ? tab : 'queue'
  return (
    <section className="flex flex-col gap-4" data-testid="review-page" data-tab={shownTab}>
      {back}
      <h1 className="text-2xl font-bold">{t('reviewQueue.title')}</h1>
      {signals && (
        <div role="tablist" aria-label={t('reviewQueue.title')} className="grid grid-cols-2 gap-2">
          {(['queue', 'signals'] as const).map((id) => (
            <button key={id} type="button" role="tab" aria-selected={shownTab === id} className={`btn !px-2 ${shownTab === id ? 'btn-primary' : ''}`} onClick={() => setTab(id)} data-testid={`review-tab-${id}`}>{t(`reviewQueue.tabs.${id}`)}</button>
          ))}
        </div>
      )}
      {shownTab === 'signals' && <SignalsTab review={q.data} online={q.online} />}
      {shownTab === 'queue' && (
        <>
          {q.online === false && <Banner tone="info"><span data-testid="review-offline">{t('reviewQueue.offline')}</span></Banner>}
          {q.error && <Banner tone="error">{errorText(t, q.error)}</Banner>}
          {done && <Banner tone="ok"><span data-testid="review-done">{done}</span></Banner>}
          {!items && q.online !== false && !q.error && <p>{t('common.loading')}</p>}
          {items && (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="font-semibold" data-testid="review-count">{t('reviewQueue.count', { count: items.length })}</p>
                <button type="button" className="btn !min-h-11 !px-4" onClick={() => { setDone(null); void q.reload() }} data-testid="review-reload">{t('reviewQueue.reload')}</button>
              </div>
              {items.length === 0 && <p className="opacity-70" data-testid="review-empty">{t('reviewQueue.empty')}</p>}
              <ul className="flex flex-col gap-3">
                {items.map((item) => (
                  <ItemCard key={item.id} item={item} tz={site.timezone} target={reviewTarget(item, sessionJob, intervalSession)} acceptable={can('worklog.review') && item.entity_type === 'ops.work_interval' && !!item.entity_id && pendingIntervals.has(item.entity_id)}
                    resolvable={can('review.resolve')} onDone={(m) => { setDone(m); void q.reload() }} />
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </section>
  )
}

/** "Signaux": the lines of "À traiter", those put aside on this device under them. Reads what Today reads (the mirror, the review list it already holds, the
 *  service due) and the hook's own three server reads; offline, the server's lines are simply absent and the tab says so. */
function SignalsTab({ review, online }: { review: ReviewItem[] | null; online: boolean }) {
  const { t, i18n } = useTranslation()
  const { site, kiosk } = useClub()
  const profile = profileOf(site.roles, !!kiosk)
  const { plan } = usePlan(i18n.language)
  const due = useServiceDue()
  const names = useNames()
  const areas = useAreas()
  const areaById = new Map((areas ?? []).map((a) => [a.id, a as AreaRow]))
  const s = useHomeSignals(profile, { jobs: plan.jobs, review, due: due.data, names: names ?? undefined })
  const line = (r: ExceptionRow, seen: boolean) => (
    <li key={r.kind} className={`card flex flex-col gap-2 ${seen ? '!border-slate-400 dark:!border-slate-500' : ''}`} data-testid="signal-line" data-kind={r.kind} data-seen={seen ? '1' : '0'} data-count={r.count}>
      <div className="flex flex-wrap items-center gap-2">
        <Pill tone={seen ? 'gray' : r.severity === 'red' ? 'red' : 'amber'}>{seen ? t('reviewQueue.signals.seen') : t(`reviewQueue.severity.${r.severity === 'red' ? 'high' : 'medium'}`)}</Pill>
        <h2 className="min-w-0 break-words text-lg font-bold" data-testid="signal-title">{t(EXCEPTION_TITLE_KEY[r.kind], { count: r.count, days: EXCEPTION_DAYS[r.kind] })}</h2>
      </div>
      <p className="meta" data-testid="signal-hint">{exceptionHint(r, areaById, i18n.language, t)}</p>
      <div className="grid grid-cols-2 gap-2">
        <Link to={r.to as never} search={r.search as never} hash={r.hash} className="btn" data-testid="signal-open">{t('common.open')}</Link>
        {seen
          ? <button type="button" className="btn" onClick={() => s.unmark(r.kind)} data-testid="signal-unsee">{t('reviewQueue.signals.unsee')}</button>
          : <button type="button" className="btn btn-primary" onClick={() => s.mark(r)} data-testid="signal-seen">{t('reviewQueue.signals.markSeen')}</button>}
      </div>
    </li>
  )
  return (
    <section className="flex flex-col gap-3" data-testid="signals-tab" data-ready={s.ready ? '1' : '0'}>
      <p className="meta">{t('reviewQueue.signals.help')}</p>
      {!online && <Banner tone="info"><span data-testid="signals-offline">{t('reviewQueue.signals.offline')}</span></Banner>}
      {s.ready && s.rows.length === 0 && <p className="opacity-70" data-testid="signals-empty">{t('reviewQueue.signals.empty')}</p>}
      <ul className="flex flex-col gap-3" data-testid="signals-shown">{s.shown.map((r) => line(r, false))}</ul>
      {s.hidden.length > 0 && (
        <>
          <h2 className="text-lg font-bold">{t('reviewQueue.signals.asideTitle')}</h2>
          <ul className="flex flex-col gap-3" data-testid="signals-hidden">{s.hidden.map((r) => line(r, true))}</ul>
        </>
      )}
      <p className="meta" data-testid="signals-rule">{t('today.triage.rule', EXCEPTION_RULE)}</p>
    </section>
  )
}

function ItemCard({ item, tz, target, acceptable, resolvable, onDone }: {
  item: ReviewItem; tz: string; target: { jobId: string | null; assetId: string | null }; acceptable: boolean; resolvable: boolean; onDone: (message: string) => void
}) {
  const { t, i18n } = useTranslation()
  const w = useWorkshopAction()
  const [open, setOpen] = useState(false)
  const [note, setNote] = useState('')
  const kind = i18n.exists(`reviewQueue.kind.${item.kind}`) ? t(`reviewQueue.kind.${item.kind}`) : t('reviewQueue.kind.other')
  // a code with its own short title, else the catalogue's sentence for it, else the kind (a command error's code is a database state: shown beside the kind, never as a title)
  const title = !item.code || item.kind === 'command_error' ? (item.code ? `${kind} (${item.code})` : kind)
    : i18n.exists(`reviewQueue.code.${item.code}`) ? t(`reviewQueue.code.${item.code}`)
    : i18n.exists(`errors.${item.code}`) ? errorText(t, item.code) : `${kind} (${item.code})`
  const what = [item.person, item.job_title ? say(item.job_title, i18n.language) : null, item.asset_tag].filter(Boolean).join(' · ')
  const span = item.interval ? spanText(item.interval.from, item.interval.to, tz) : null
  const finish = (messageKey: string) => (ok: boolean) => { if (ok) onDone(t(messageKey)) }
  const decide = (decision: 'accepted' | 'rejected') => void w.run('work.review@1', { interval_id: item.entity_id!, decision }, `reviewQueue.${decision}`).then(finish(`reviewQueue.${decision}`))
  const resolve = () => void w.run('review.resolve@1', { review_item_id: item.id, resolution: 'handled', note: note.trim() || null }, 'reviewQueue.resolved').then(finish('reviewQueue.resolved'))
  return (
    <li className="card flex flex-col gap-2" data-testid="review-item" data-id={item.id} data-kind={item.kind} data-code={item.code ?? ''}>
      <div className="flex flex-wrap items-center gap-2">
        <Pill tone={SEVERITY_TONE[item.severity] ?? 'gray'}>{t(`reviewQueue.severity.${item.severity}`)}</Pill>
        <h2 className="min-w-0 break-words text-lg font-bold" data-testid="review-title">{title}</h2>
      </div>
      {what && <p className="font-semibold" data-testid="review-what">{what}</p>}
      {item.interval && span && (
        <p className="text-sm" data-testid="review-interval">
          {span.to ? t('common.fromTo', { from: span.from, to: span.to }) : t('common.since', { when: span.from })}
          {item.interval.source !== 'timer' && ` · ${t(`myTime.source.${item.interval.source}`, { defaultValue: item.interval.source })}`}
        </p>
      )}
      <p className="meta">{t('reviewQueue.raised', { when: formatLocal(item.created_at, tz, 'dd/MM/yyyy HH:mm') })}</p>
      {item.entity_type === 'ops.job_settings_confirmation' && <SettingsItemLinks item={item} />}
      {(target.jobId || target.assetId) && (
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {target.jobId && <Link to="/plan/job/$jobId" params={{ jobId: target.jobId }} className="btn !px-4" data-testid="review-job-link">{t('reviewQueue.openJob')}</Link>}
          {target.assetId && <Link to="/assets/$assetId" params={{ assetId: target.assetId }} className="btn !px-4" data-testid="review-asset-link">{t('reviewQueue.openMachine')}</Link>}
        </div>
      )}
      {acceptable && (
        <div className="grid grid-cols-2 gap-2">
          <button type="button" className="btn btn-primary !min-h-11" disabled={w.busy} onClick={() => decide('accepted')} data-testid="review-accept">{t('reviewQueue.accept')}</button>
          <button type="button" className="btn !min-h-11" disabled={w.busy} onClick={() => decide('rejected')} data-testid="review-reject">{t('reviewQueue.reject')}</button>
        </div>
      )}
      {resolvable && !open && <button type="button" className="btn !min-h-11 self-start !px-4" onClick={() => setOpen(true)} data-testid="review-resolve">{t('reviewQueue.handled')}</button>}
      {resolvable && open && (
        <form className="flex flex-col gap-3 rounded-xl border-2 border-slate-300 p-3 dark:border-slate-600" onSubmit={(ev) => { ev.preventDefault(); resolve() }}>
          <Field label={t('reviewQueue.note')} htmlFor={`rn-${item.id}`}>
            <textarea id={`rn-${item.id}`} className="input min-h-20" maxLength={300} value={note} onChange={(ev) => setNote(ev.target.value)} data-testid="review-note" />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button type="submit" className="btn btn-primary !min-h-11" disabled={w.busy} data-testid="review-resolve-go">{w.busy ? t('common.saving') : t('reviewQueue.handledGo')}</button>
            <button type="button" className="btn !min-h-11" disabled={w.busy} onClick={() => setOpen(false)}>{t('common.cancel')}</button>
          </div>
        </form>
      )}
      {w.pending && <PendingBanner />}
      {w.error && <Banner tone="error"><span data-testid="review-error">{w.error}</span></Banner>}
    </li>
  )
}

/** M25-F (docs/M25.md B, "What the office sees of the two soft codes"): an item about a settings confirmation says what happened in the club's words, links to
 *  its job through the device's own copy of the row (the direction holds it; `reviewTarget`: no copy, no link, never guessed) and, for a word that is no longer
 *  a choice, to the task types where the club's words are kept. There is nothing to correct: the office closes the item with « Traité » (review.resolve@1). Its own
 *  component so that the hook reads the mirror only for such an item. */
function SettingsItemLinks({ item }: { item: ReviewItem }) {
  const { t } = useTranslation()
  const rows = useSettingsConfirmations()
  const target = reviewTarget(item, new Map(), new Map(), new Map((rows ?? []).map((r) => [r.id, r.job_id])))
  const unknown = item.code === 'SETTINGS_CHOICE_UNKNOWN', elsewhere = item.code === 'SETTINGS_CONFIRMED_ELSEWHERE'
  return (
    <>
      {(unknown || elsewhere) && <p className="meta" data-testid="review-settings-hint">{t(unknown ? 'reviewQueue.settingsUnknownHint' : 'reviewQueue.settingsElsewhereHint')}</p>}
      {(target.jobId || unknown) && (
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {target.jobId && <Link to="/plan/job/$jobId" params={{ jobId: target.jobId }} className="btn !px-4" data-testid="review-job-link">{t('reviewQueue.openJob')}</Link>}
          {unknown && <Link to="/plan/setup" className="btn !px-4" data-testid="review-types-link">{t('reviewQueue.openTypes')}</Link>}
        </div>
      )}
    </>
  )
}
